import React, { useCallback, useMemo, useState } from "react";
import { LanguagesIcon } from "lucide-react";
import { useLearner } from "../../contexts/LearnerContext";
import { useWords } from "../../contexts/WordsContext";
import { units } from "../../data/courses";
import { lessonSpeechLang, marketLesson } from "../../data/lessonContent";
import { useLessonRunner } from "../../hooks/useLessonRunner";
import { LessonShell } from "./LessonShell";
import { ExerciseView, exerciseHeadings } from "./ExerciseView";
import { LessonComplete } from "./LessonComplete";
import { Tag } from "../ui/Tag";
import type { Exercise } from "../../types/exercises";
import type { Course, Skills } from "../../types/learning";
import { buildPracticeLesson, termsIn, translatorItemsIn } from "../../utils/practice";
import { getLanguage } from "../../utils/language";
import { skillLabels } from "../../utils/course";
import { learnPath } from "../../utils/routes";
interface LessonRunnerProps {
  course: Course;
  lessonId: string;
  practice: boolean;
}
interface LessonSetup {
  exercises: Exercise[];
  lang: string;
  title: string;
  advance: boolean;
}
export function LessonRunner({
  course,
  lessonId,
  practice
}: LessonRunnerProps) {
  const {
    plan
  } = useLearner();
  const {
    dueWords,
    recordPractice
  } = useWords();
  const [setup] = useState<LessonSetup>(() => {
    if (practice) {
      const built = buildPracticeLesson(dueWords(course.id));
      if (built) return {
        exercises: built,
        lang: getLanguage(course.target).speech,
        title: "My Words practice",
        advance: false
      };
    }
    const focus = lessonId.startsWith("focus-") ? lessonId.slice(6) as keyof Skills : null;
    const lessonTitle = units.flatMap(u => u.lessons).find(l => l.id === lessonId)?.title;
    return {
      exercises: marketLesson,
      lang: lessonSpeechLang,
      title: focus ? `${skillLabels[focus] ?? "Skill"} focus` : practice ? "Practice" : lessonTitle ?? "Practice",
      advance: !focus && !practice
    };
  });
  const onChecked = useCallback((ex: Exercise, correct: boolean) => recordPractice(termsIn(ex), correct), [recordPractice]);
  const runner = useLessonRunner(setup.exercises, plan === "plus", onChecked);
  const translatorItems = useMemo(() => setup.exercises.reduce((n, ex) => n + translatorItemsIn(ex), 0), [setup.exercises]);
  const backTo = learnPath(course);
  if (runner.finished) {
    return <LessonComplete courseId={course.id} continueTo={backTo} xp={runner.xp} accuracy={runner.accuracy} seconds={(Date.now() - runner.startedAt) / 1000} translatorItems={translatorItems} advance={setup.advance} title={setup.title} data-id="element-338" data-mp="3h5sprayonmuh" />;
  }
  const ex = runner.exercise;
  return <LessonShell stepKey={ex.id} progress={runner.progress} hearts={runner.hearts} unlimited={plan === "plus"} phase={runner.phase} answer={runner.answer} skipped={runner.skipped} exitTo={backTo} onCheck={() => runner.check()} onSkip={runner.skip} onContinue={runner.next} onRefill={runner.refillHearts} target={course.target} heading={<>
          <h1 className="text-2xl font-bold tracking-[-0.01em] md:text-3xl" data-id="element-340" data-mp="9dosadl8vfyq">{exerciseHeadings[ex.type]}</h1>
          {translatorItemsIn(ex) > 0 && <Tag tone="accent" icon={LanguagesIcon} data-id="element-341" data-mp="eaoqk2burefa">
              From your translator
            </Tag>}
        </>} data-id="element-339" data-mp="3lz41nqicqtt9">
      <ExerciseView exercise={ex} checked={runner.phase === "checked"} onAnswer={runner.setAnswer} speechLang={setup.lang} data-id="element-342" data-mp="4e26vgv9iteu" />
    </LessonShell>;
}